import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Globe, Info, Play } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';

const VisualScheduleBuilder = ({ value, onChange, workflowId }) => {
  const [scheduleType, setScheduleType] = useState('daily');
  const [dailyTime, setDailyTime] = useState('09:00');
  const [hourlyInterval, setHourlyInterval] = useState(1);
  const [minuteInterval, setMinuteInterval] = useState(30);
  const [weeklyDay, setWeeklyDay] = useState('1'); // Monday
  const [weeklyTime, setWeeklyTime] = useState('09:00');
  const [monthlyDay, setMonthlyDay] = useState('1');
  const [monthlyTime, setMonthlyTime] = useState('09:00');
  const [customCron, setCustomCron] = useState('');
  const [userTimezone, setUserTimezone] = useState('UTC');
  const [nextRun, setNextRun] = useState(null);
  const [lastRun, setLastRun] = useState(null);

  useEffect(() => {
    // Detect user timezone
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setUserTimezone(tz);
  }, []);

  useEffect(() => {
    // Parse existing cron value if provided
    if (value && typeof value === 'string' && value !== generateCurrentCron()) {
      parseCronToUI(value);
    }
  }, [value]);

  // Helper to get current cron without triggering updates
  const generateCurrentCron = () => {
    switch (scheduleType) {
      case 'daily': {
        const [hour, minute] = dailyTime.split(':');
        return `${minute} ${hour} * * *`;
      }
      case 'hourly': {
        return `0 */${hourlyInterval} * * *`;
      }
      case 'minutes': {
        return `*/${minuteInterval} * * * *`;
      }
      case 'weekly': {
        const [hour, minute] = weeklyTime.split(':');
        return `${minute} ${hour} * * ${weeklyDay}`;
      }
      case 'monthly': {
        const [hour, minute] = monthlyTime.split(':');
        return `${minute} ${hour} ${monthlyDay} * *`;
      }
      case 'custom':
        return customCron;
      default:
        return '0 9 * * *';
    }
  };

  // Only notify parent when user makes changes, not on every render
  const handleScheduleChange = (newScheduleType) => {
    setScheduleType(newScheduleType);
    // Calculate and send new cron after state settles
    setTimeout(() => {
      const cron = generateCurrentCron();
      if (onChange) {
        onChange(cron);
      }
      calculateNextRun(cron);
    }, 0);
  };

  const handleTimeChange = (setter, value) => {
    setter(value);
    setTimeout(() => {
      const cron = generateCurrentCron();
      if (onChange) {
        onChange(cron);
      }
      calculateNextRun(cron);
    }, 0);
  };

  const parseCronToUI = (cronExpr) => {
    const patterns = {
      '0 * * * *': { type: 'hourly', hourly: 1 },
      '*/30 * * * *': { type: 'minutes', minutes: 30 },
      '*/15 * * * *': { type: 'minutes', minutes: 15 },
    };

    if (patterns[cronExpr]) {
      const pattern = patterns[cronExpr];
      setScheduleType(pattern.type);
      if (pattern.hourly) setHourlyInterval(pattern.hourly);
      if (pattern.minutes) setMinuteInterval(pattern.minutes);
    } else if (cronExpr.match(/^\d+ \d+ \* \* \*$/)) {
      // Daily pattern: "0 9 * * *"
      const parts = cronExpr.split(' ');
      setScheduleType('daily');
      setDailyTime(`${parts[1].padStart(2, '0')}:${parts[0].padStart(2, '0')}`);
    } else if (cronExpr.match(/^\d+ \d+ \* \* \d+$/)) {
      // Weekly pattern: "0 9 * * 1"
      const parts = cronExpr.split(' ');
      setScheduleType('weekly');
      setWeeklyDay(parts[4]);
      setWeeklyTime(`${parts[1].padStart(2, '0')}:${parts[0].padStart(2, '0')}`);
    } else if (cronExpr.match(/^\d+ \d+ \d+ \* \*$/)) {
      // Monthly pattern: "0 9 1 * *"
      const parts = cronExpr.split(' ');
      setScheduleType('monthly');
      setMonthlyDay(parts[2]);
      setMonthlyTime(`${parts[1].padStart(2, '0')}:${parts[0].padStart(2, '0')}`);
    } else {
      setScheduleType('custom');
      setCustomCron(cronExpr);
    }
  };

  const generateCronExpression = () => {
    switch (scheduleType) {
      case 'daily': {
        const [hour, minute] = dailyTime.split(':');
        return `${minute} ${hour} * * *`;
      }
      case 'hourly': {
        return `0 */${hourlyInterval} * * *`;
      }
      case 'minutes': {
        return `*/${minuteInterval} * * * *`;
      }
      case 'weekly': {
        const [hour, minute] = weeklyTime.split(':');
        return `${minute} ${hour} * * ${weeklyDay}`;
      }
      case 'monthly': {
        const [hour, minute] = monthlyTime.split(':');
        return `${minute} ${hour} ${monthlyDay} * *`;
      }
      case 'custom':
        return customCron;
      default:
        return '0 9 * * *';
    }
  };

  const calculateNextRun = (cronExpr) => {
    // Parse cron and calculate next run time
    // This is a simplified calculation - in production would use a proper cron parser
    try {
      const now = new Date();
      let next = new Date(now);

      if (scheduleType === 'daily') {
        const [hour, minute] = dailyTime.split(':');
        next.setHours(parseInt(hour), parseInt(minute), 0, 0);
        if (next <= now) {
          next.setDate(next.getDate() + 1);
        }
      } else if (scheduleType === 'hourly') {
        next.setMinutes(0, 0, 0);
        next.setHours(next.getHours() + hourlyInterval);
      } else if (scheduleType === 'minutes') {
        next.setSeconds(0, 0);
        next.setMinutes(next.getMinutes() + minuteInterval);
      } else if (scheduleType === 'weekly') {
        const [hour, minute] = weeklyTime.split(':');
        next.setHours(parseInt(hour), parseInt(minute), 0, 0);
        const currentDay = next.getDay();
        const targetDay = parseInt(weeklyDay);
        const daysUntil = (targetDay - currentDay + 7) % 7 || 7;
        next.setDate(next.getDate() + daysUntil);
      } else if (scheduleType === 'monthly') {
        const [hour, minute] = monthlyTime.split(':');
        next.setDate(parseInt(monthlyDay));
        next.setHours(parseInt(hour), parseInt(minute), 0, 0);
        if (next <= now) {
          next.setMonth(next.getMonth() + 1);
        }
      }

      setNextRun(next);
    } catch (error) {
      console.error('Error calculating next run:', error);
      setNextRun(null);
    }
  };

  const formatNextRun = () => {
    if (!nextRun) return 'Not scheduled';
    
    const now = new Date();
    const diffMs = nextRun - now;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    let relative = '';
    if (diffMins < 60) relative = `in ${diffMins} minutes`;
    else if (diffHours < 24) relative = `in ${diffHours} hours`;
    else if (diffDays < 7) relative = `in ${diffDays} days`;
    else relative = nextRun.toLocaleDateString();

    const localTime = nextRun.toLocaleString('en-US', {
      timeZone: userTimezone,
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    return `${localTime} (${relative})`;
  };

  const weekDays = [
    { value: '1', label: 'Monday' },
    { value: '2', label: 'Tuesday' },
    { value: '3', label: 'Wednesday' },
    { value: '4', label: 'Thursday' },
    { value: '5', label: 'Friday' },
    { value: '6', label: 'Saturday' },
    { value: '0', label: 'Sunday' }
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Schedule Builder
        </CardTitle>
        <CardDescription>
          Configure when this workflow should run automatically
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Timezone Display */}
        <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <Globe className="w-4 h-4 text-blue-600" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-900">Your Timezone</p>
            <p className="text-xs text-blue-700">{userTimezone}</p>
          </div>
        </div>

        {/* Schedule Type Selection */}
        <div className="space-y-4">
          <Label className="text-base font-semibold">Schedule Type</Label>
          <RadioGroup value={scheduleType} onValueChange={handleScheduleChange}>
            {/* Daily */}
            <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
              <RadioGroupItem value="daily" id="daily" />
              <Label htmlFor="daily" className="flex-1 cursor-pointer">
                <p className="font-medium">Daily</p>
                <p className="text-xs text-muted-foreground">Run every day at a specific time</p>
              </Label>
            </div>

            {scheduleType === 'daily' && (
              <div className="ml-10 mt-2 space-y-2">
                <Label className="text-sm">Time</Label>
                <Input
                  type="time"
                  value={dailyTime}
                  onChange={(e) => handleTimeChange(setDailyTime, e.target.value)}
                  className="w-40"
                />
              </div>
            )}

            {/* Hourly */}
            <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
              <RadioGroupItem value="hourly" id="hourly" />
              <Label htmlFor="hourly" className="flex-1 cursor-pointer">
                <p className="font-medium">Hourly</p>
                <p className="text-xs text-muted-foreground">Run every X hours</p>
              </Label>
            </div>

            {scheduleType === 'hourly' && (
              <div className="ml-10 mt-2 space-y-2">
                <Label className="text-sm">Every</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="1"
                    max="24"
                    value={hourlyInterval}
                    onChange={(e) => handleTimeChange(setHourlyInterval, parseInt(e.target.value) || 1)}
                    className="w-20"
                  />
                  <span className="text-sm text-muted-foreground">hours</span>
                </div>
              </div>
            )}

            {/* Minutes */}
            <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
              <RadioGroupItem value="minutes" id="minutes" />
              <Label htmlFor="minutes" className="flex-1 cursor-pointer">
                <p className="font-medium">Minutes</p>
                <p className="text-xs text-muted-foreground">Run every X minutes</p>
              </Label>
            </div>

            {scheduleType === 'minutes' && (
              <div className="ml-10 mt-2 space-y-2">
                <Label className="text-sm">Every</Label>
                <Select
                  value={minuteInterval.toString()}
                  onValueChange={(v) => handleTimeChange(setMinuteInterval, parseInt(v))}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 minutes</SelectItem>
                    <SelectItem value="10">10 minutes</SelectItem>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="45">45 minutes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Weekly */}
            <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
              <RadioGroupItem value="weekly" id="weekly" />
              <Label htmlFor="weekly" className="flex-1 cursor-pointer">
                <p className="font-medium">Weekly</p>
                <p className="text-xs text-muted-foreground">Run on specific day each week</p>
              </Label>
            </div>

            {scheduleType === 'weekly' && (
              <div className="ml-10 mt-2 space-y-3">
                <div className="space-y-2">
                  <Label className="text-sm">Day of Week</Label>
                  <Select value={weeklyDay} onValueChange={(v) => handleTimeChange(setWeeklyDay, v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {weekDays.map(day => (
                        <SelectItem key={day.value} value={day.value}>
                          {day.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Time</Label>
                  <Input
                    type="time"
                    value={weeklyTime}
                    onChange={(e) => handleTimeChange(setWeeklyTime, e.target.value)}
                    className="w-40"
                  />
                </div>
              </div>
            )}

            {/* Monthly */}
            <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
              <RadioGroupItem value="monthly" id="monthly" />
              <Label htmlFor="monthly" className="flex-1 cursor-pointer">
                <p className="font-medium">Monthly</p>
                <p className="text-xs text-muted-foreground">Run on specific day each month</p>
              </Label>
            </div>

            {scheduleType === 'monthly' && (
              <div className="ml-10 mt-2 space-y-3">
                <div className="space-y-2">
                  <Label className="text-sm">Day of Month</Label>
                  <Input
                    type="number"
                    min="1"
                    max="31"
                    value={monthlyDay}
                    onChange={(e) => handleTimeChange(setMonthlyDay, e.target.value)}
                    className="w-20"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Time</Label>
                  <Input
                    type="time"
                    value={monthlyTime}
                    onChange={(e) => handleTimeChange(setMonthlyTime, e.target.value)}
                    className="w-40"
                  />
                </div>
              </div>
            )}

            {/* Custom Cron */}
            <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
              <RadioGroupItem value="custom" id="custom" />
              <Label htmlFor="custom" className="flex-1 cursor-pointer">
                <p className="font-medium">Custom Cron Expression</p>
                <p className="text-xs text-muted-foreground">Advanced: Use cron syntax</p>
              </Label>
            </div>

            {scheduleType === 'custom' && (
              <div className="ml-10 mt-2 space-y-2">
                <Label className="text-sm">Cron Expression</Label>
                <Input
                  value={customCron}
                  onChange={(e) => handleTimeChange(setCustomCron, e.target.value)}
                  placeholder="* * * * *"
                  className="font-mono"
                />
                <p className="text-xs text-muted-foreground">
                  Format: minute hour day month dayOfWeek
                </p>
                <a 
                  href="https://crontab.guru" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                >
                  <Info className="w-3 h-3" />
                  Learn cron syntax
                </a>
              </div>
            )}
          </RadioGroup>
        </div>

        {/* Schedule Preview */}
        <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg space-y-3">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-purple-600" />
            <p className="font-semibold text-sm text-purple-900">Schedule Preview</p>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">Cron Expression:</span>
              <code className="text-xs font-mono bg-white px-2 py-1 rounded border">
                {generateCurrentCron()}
              </code>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">Next Run:</span>
              <span className="text-xs font-semibold text-purple-700">
                {formatNextRun()}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">Timezone:</span>
              <span className="text-xs font-medium">{userTimezone}</span>
            </div>

            {lastRun && (
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="text-xs text-muted-foreground">Last Run:</span>
                <span className="text-xs">
                  {new Date(lastRun).toLocaleString('en-US', { timeZone: userTimezone })}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Human Readable Description */}
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground">
            <strong>In plain English:</strong> {getHumanReadable()}
          </p>
        </div>
      </CardContent>
    </Card>
  );

  function getHumanReadable() {
    switch (scheduleType) {
      case 'daily':
        return `Runs every day at ${dailyTime} ${userTimezone}`;
      case 'hourly':
        return hourlyInterval === 1 
          ? 'Runs every hour'
          : `Runs every ${hourlyInterval} hours`;
      case 'minutes':
        return `Runs every ${minuteInterval} minutes`;
      case 'weekly': {
        const dayName = weekDays.find(d => d.value === weeklyDay)?.label;
        return `Runs every ${dayName} at ${weeklyTime} ${userTimezone}`;
      }
      case 'monthly':
        return `Runs on day ${monthlyDay} of each month at ${monthlyTime} ${userTimezone}`;
      case 'custom':
        return customCron || 'No expression set';
      default:
        return 'No schedule configured';
    }
  }
};

export default VisualScheduleBuilder;
